import { useState, useEffect } from "react";
import { Users, CalendarCheck, RefreshCw, Clock } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const [queue, setQueue] = useState([]);
  const [stats, setStats] = useState({ checkins_today: 0, reschedules_today: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [queueRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/queue`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/stats`, { withCredentials: true }),
      ]);

      if (queueRes.data.success) {
        setQueue(queueRes.data.queue);
      }
      if (statsRes.data.success) {
        setStats(statsRes.data.stats);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success("Dados atualizados!");
  };

  const formatTime = (isoString) => {
    if (!isoString) return "-";
    const date = new Date(isoString);
    return date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "aguardando":
        return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Aguardando</Badge>;
      case "atendimento":
        return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Em Atendimento</Badge>;
      case "concluido":
        return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Concluído</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 font-['Outfit']">
              Fila de Hoje
            </h1>
            <p className="text-slate-500 mt-1">
              Acompanhe os pacientes que fizeram check-in
            </p>
          </div>
          <Button
            onClick={handleRefresh}
            disabled={refreshing}
            variant="outline"
            className="flex items-center gap-2"
            data-testid="refresh-queue-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            Atualizar
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="admin-stat-card">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-[#00357C]/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-[#00357C]" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Aguardando</p>
                <p className="text-3xl font-bold text-slate-800">{queue.length}</p>
              </div>
            </div>
          </div>

          <div className="admin-stat-card">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                <CalendarCheck className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Check-ins Hoje</p>
                <p className="text-3xl font-bold text-slate-800">{stats.checkins_today}</p>
              </div>
            </div>
          </div>

          <div className="admin-stat-card">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <RefreshCw className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Remarcações Hoje</p>
                <p className="text-3xl font-bold text-slate-800">{stats.reschedules_today}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Queue Table */}
        <div className="admin-card">
          <h2 className="text-xl font-semibold text-slate-800 mb-6 font-['Outfit']">
            Pacientes Aguardando
          </h2>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner" />
            </div>
          ) : queue.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-lg text-slate-500">Nenhum paciente na fila</p>
              <p className="text-sm text-slate-400 mt-1">
                Os pacientes aparecerão aqui após fazer check-in no totem
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID Agendamento</TableHead>
                  <TableHead>ID Paciente</TableHead>
                  <TableHead>Chegada</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {queue.map((item) => (
                  <TableRow key={item.id} data-testid={`queue-row-${item.id}`}>
                    <TableCell className="font-medium">#{item.appointment_id}</TableCell>
                    <TableCell>{item.patient_id}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-slate-400" />
                        {formatTime(item.checkin_time)}
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(item.status)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
